import {Injectable} from "@angular/core";
import {UtilsService} from "./utils.service";
import {GlobalStorageService} from "./global-storage.service";

@Injectable({
  providedIn: 'root'
})
export class IndexedDB {
  db: any;
  self = this;
  private static indexedDB: any;
  tables: string[] = [
    'lastUpdate',
    'visits',
    'notes',
    'districts',
    'sites',
    'routes',
    'educationModulesCategories',
    'questionTemplates',
    'questionsCategories',
    'routeTemplates',
    'serviceCategories',
    'sitesTemplates',
    'trainingCategories',
    'clockInOut',
    'notesToDelete',
    'truckInventoryTemplates',
    'inventoryServiceComments'
  ];
  dbName: string = 'fss';
  dbVersion: number = 1;

  constructor(
    private utils: UtilsService,
    private global: GlobalStorageService
  ) {
  }

  initTable(db: any, name: string) {
    if (db.objectStoreNames.contains(name)) {
      db.deleteObjectStore(name);
    }
    const store = db.createObjectStore(name, {
      keyPath: "id"
    });
    store.createIndex('id', 'id', {
      unique: true
    });
  }

  public init() {
    const self = this;
    return new Promise((resolve, reject) => {
      IndexedDB.indexedDB = {};
      IndexedDB.indexedDB.db = null;
      let request = indexedDB.open(self.dbName, self.dbVersion);

      // We can only create Object stores in a versionchange transaction.
      request.onupgradeneeded = function (e) {
        let db = (<any>e.target).result;

        for (let table of self.tables) {
          self.initTable(db, table);
        }
      };

      request.onsuccess = function (e) {
        IndexedDB.indexedDB.db = (<any>e.target).result;
        resolve(IndexedDB.indexedDB.db);
      };

      request.onerror = IndexedDB.onerror;
    });
  }

  public setDBName(name: string) {
    this.dbName = name;
  }

  private static onerror(err: any) {
    alert(err);
  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,
      function (c) {
        let uuid = Math.random() * 16 | 0,
          v = c == 'x' ? uuid : (uuid & 0x3 | 0x8);
        return uuid.toString(16);
      });
  }

  /* createItem */
  public createItem(tableName: string, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        data._createdDate = this.utils.getPacificDate();
        data._updatedDate = this.utils.getPacificDate();
        let trans = db.transaction([tableName], "readwrite");
        let store = trans.objectStore(tableName);
        let request = store.put(data);
        request.onsuccess = function (success: any) {
          resolve(data);
        };
        request.onerror = function (e: any) {
          reject(e);
        };
      } catch (e: any) {
        alert(`Can't create ${tableName}, code "1"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItems(tableName: string) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        let trans = db.transaction(tableName); // readonly
        let store = trans.objectStore(tableName);
        let index = store.index("id");
        let request = index.getAll();
        request.onsuccess = function () {
          resolve(request.result);
        }
      } catch (e: any) {
        alert(`Can't read ${tableName}, code "2"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItem(tableName: string, id: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        let trans = db.transaction(tableName); // readonly
        let store = trans.objectStore(tableName);
        let index = store.index("id");
        let request = index.get(id);
        request.onsuccess = function () {
          resolve(request.result);
        }
        request.onerror = function () {
          reject({});
        }
      } catch (e: any) {
        alert(`Can't read ${tableName}, #${id}, code "3"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItemsByField(tableName: string, fieldName: any, fieldValue: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        let trans = db.transaction(tableName); // readonly
        let objectStore = trans.objectStore(tableName);
        const items: any[] = [];
        let request = objectStore.openCursor();

        request.onsuccess = function () {
          let cursor = request.result;
          if (cursor) {
            let value = cursor.value; // object
            if (value[fieldName] === fieldValue) {
              items.push(value);
            }
            cursor.continue();
          } else {
            resolve(items);
            //console.log("No more items");
          }
        };
      } catch (e: any) {
        alert(`Can't read ${tableName}, ${fieldName}, ${fieldValue}, code "4"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  /*
    Read items in the @tableName by the @fieldName and @subfieldName like obj.fieldName.subfieldName
   */
  public readItemsByFieldValue(tableName: string, fieldName: any, subfieldName: any, fieldValue: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        let trans = db.transaction(tableName); // readonly
        let objectStore = trans.objectStore(tableName);
        const items: any[] = [];
        let request = objectStore.openCursor();

        request.onsuccess = function () {
          let cursor = request.result;
          if (cursor) {
            let value = cursor.value; // object
            if (value[fieldName] && value[fieldName][subfieldName] === fieldValue) {
              items.push(value);
            }
            cursor.continue();
          } else {
            resolve(items);
            //console.log("No more items");
          }
        };
      } catch (e: any) {
        alert(`Can't read ${tableName}, ${fieldName}, ${subfieldName}, ${fieldValue}, code "5"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItemsRange(tableName: string, key: string, from: any, to: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();
        const transaction = db.transaction(tableName, "readonly");
        const objectStore = transaction.objectStore(tableName);
        const items: any[] = [];
        let request = objectStore.openCursor();

        request.onsuccess = function () {
          let cursor = request.result;
          if (cursor) {
            let keyField = cursor.key; // key (id field)
            let value = cursor.value; // object
            if (value[key] > from && value[key] < to) {
              items.push(value);
            }
            cursor.continue();
          } else {
            resolve(items);
            //console.log("No more items");
          }
        };
      } catch (e: any) {
        alert(`Can't read ${tableName}, ${key}, ${from}, ${to}, code "6"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItemsFrom(tableName: string, key: string, from: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();

        const transaction = db.transaction(tableName, "readonly");
        const objectStore = transaction.objectStore(tableName);
        const items: any[] = [];
        let request = objectStore.openCursor();

        request.onsuccess = function () {
          let cursor = request.result;
          if (cursor) {
            let keyField = cursor.key; // key (id field)
            let value = cursor.value; // object
            if (value[key] > from) {
              items.push(value);
            }

            cursor.continue();
          } else {
            resolve(items);
            //console.log("No more items");
          }
        };
      } catch (e: any) {
        alert(`Can't read ${tableName}, ${key}, ${from}, code "7"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public readItemsByIds(tableName: string, ids: any = []) {
    return new Promise(async (resolve, reject) => {
      try {
        if (ids.length) {
          let db: any = await this.init();

          const transaction = db.transaction(tableName, "readonly");
          const objectStore = transaction.objectStore(tableName);
          const items: any[] = [];
          let request = objectStore.openCursor();

          request.onsuccess = function () {
            let cursor = request.result;
            if (cursor) {
              let keyField = cursor.key; // key (id field)
              let value = cursor.value; // object
              if (ids.includes(value.id)) {
                items.push(value);
              }
              cursor.continue();
            } else {
              resolve(items);
              //console.log("No more items");
            }
          };
        } else {
          resolve([]);
        }
      } catch (e: any) {
        alert(`Can't read ${tableName}, ${ids}, code "8"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public updateItem(tableName: string, id: any, item: any, isSynchronized: boolean = false) {
    return new Promise(async (resolve, reject) => {
      try {
        const oldItem: any = await this.readItem(tableName, id);

        let db: any = await this.init();
        let trans = db.transaction([tableName], "readwrite");
        let store = trans.objectStore(tableName);
        item._updatedDate = this.utils.getPacificDate();
        item.isSynchronized = isSynchronized;

        if (oldItem?.id) {
          let response = await store.put(item);
          resolve(response);
        } else {
          resolve({message: `${tableName} #${id} not found`});
        }
      } catch (e: any) {
        alert(`Can't update ${tableName}, #${id}, ${JSON.stringify(item)}, code "9"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }

  public deleteItem(tableName: string, id: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let db: any = await this.init();

        this.readItem(tableName, id).then((item: any) => {
          if (!item?.isSync) {
            const request = db.transaction(tableName, 'readwrite').objectStore(tableName).delete(id);
            request.onsuccess = (data: any) => {
              resolve(data);
            }
            request.onerror = (e: any) => {
              reject(e);
            }
          } else {
            item.isDeleted = true;
            this.updateItem(tableName, id, item).then((data: any) => {
              resolve(data);
            });
          }
        });
      } catch (e: any) {
        alert(`Can't delete ${tableName}, ${id}, code "10"`);
        this.global.set('error', JSON.stringify(e));
        reject(e);
      }
    });
  }
}
